import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterOutlet } from '@angular/router';

import { AuthService } from './core/auth.service';
import { I18nService } from './core/i18n.service';
import { ThemeService } from './core/theme.service';
import { TranslatePipe } from './core/translate.pipe';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterOutlet, TranslatePipe],
  template: `
    <div class="shell" [class.sidebar-collapsed]="sidebarCollapsed" *ngIf="auth.isAuthenticated; else publicPage">
      <aside class="sidebar" [class.is-collapsed]="sidebarCollapsed">
        <div class="sidebar-header">
          <div class="brand">
            <img class="brand-logo" src="assets/ofs-logo.png" alt="Odisha Fire & Emergency Services logo">
            <div class="brand-copy">
              <strong>{{ 'app.brand' | t }}</strong>
              <small>{{ 'app.name' | t }}</small>
            </div>
          </div>
          <button
            type="button"
            class="sidebar-toggle"
            (click)="toggleSidebar()"
            [attr.aria-expanded]="!sidebarCollapsed"
            [attr.aria-label]="sidebarCollapsed ? ('app.expandSidePanel' | t) : ('app.collapseSidePanel' | t)"
            [title]="sidebarCollapsed ? ('app.expandSidePanel' | t) : ('app.collapseSidePanel' | t)">
            <span class="button-icon">{{ sidebarCollapsed ? '>>' : '<<' }}</span>
            <span class="sidebar-toggle-text">{{ sidebarCollapsed ? ('app.expand' | t) : ('app.collapse' | t) }}</span>
          </button>
        </div>
        <nav aria-label="Primary navigation">
          <div class="nav-section" *ngIf="auth.hasAccess('dashboard') || auth.hasAccess('requisitions') || auth.hasAccess('financial_management')">
            <button
              type="button"
              class="nav-section-toggle"
              (click)="toggleSection('main')"
              [attr.aria-expanded]="!isSectionCollapsed('main')"
              [title]="'nav.main' | t">
              <span class="nav-group-text">{{ 'nav.main' | t }}</span>
              <span class="nav-chevron">{{ isSectionCollapsed('main') ? '+' : '-' }}</span>
            </button>
            <div class="nav-section-links" *ngIf="!isSectionCollapsed('main')">
              <a routerLink="/" *ngIf="auth.hasAccess('dashboard')" [title]="'nav.dashboard' | t">
                <span class="nav-link-mark">DB</span>
                <span class="nav-link-text">{{ 'nav.dashboard' | t }}</span>
              </a>
              <a routerLink="/lifecycle/requisitions" *ngIf="auth.hasAccess('requisitions')" [title]="'nav.requisitionRegister' | t">
                <span class="nav-link-mark">RR</span>
                <span class="nav-link-text">{{ 'nav.requisitionRegister' | t }}</span>
              </a>
              <a routerLink="/financial-management" *ngIf="auth.hasAccess('financial_management')" [title]="'nav.financialManagement' | t">
                <span class="nav-link-mark">FM</span>
                <span class="nav-link-text">{{ 'nav.financialManagement' | t }}</span>
              </a>
            </div>
          </div>

          <div class="nav-section" *ngIf="auth.hasAccess('procurement')">
            <button
              type="button"
              class="nav-section-toggle"
              (click)="toggleSection('procurement')"
              [attr.aria-expanded]="!isSectionCollapsed('procurement')"
              [title]="'nav.procurementRegister' | t">
              <span class="nav-group-text">{{ 'nav.procurementRegister' | t }}</span>
              <span class="nav-chevron">{{ isSectionCollapsed('procurement') ? '+' : '-' }}</span>
            </button>
            <div class="nav-section-links" *ngIf="!isSectionCollapsed('procurement')">
              <a routerLink="/lifecycle/procurement-cases" [title]="'nav.procurementCaseRegister' | t">
                <span class="nav-link-mark">PC</span>
                <span class="nav-link-text">{{ 'nav.procurementCaseRegister' | t }}</span>
              </a>
              <a routerLink="/lifecycle/gem-cases" [title]="'nav.gemProcurement' | t">
                <span class="nav-link-mark">GP</span>
                <span class="nav-link-text">{{ 'nav.gemProcurement' | t }}</span>
              </a>
              <a routerLink="/lifecycle/e-tender-cases" [title]="'nav.eTendering' | t">
                <span class="nav-link-mark">ET</span>
                <span class="nav-link-text">{{ 'nav.eTendering' | t }}</span>
              </a>
              <a routerLink="/lifecycle/global-tender-cases" [title]="'nav.globalTender' | t">
                <span class="nav-link-mark">GT</span>
                <span class="nav-link-text">{{ 'nav.globalTender' | t }}</span>
              </a>
              <a routerLink="/lifecycle/lpc-cases" [title]="'nav.lpc' | t">
                <span class="nav-link-mark">LP</span>
                <span class="nav-link-text">{{ 'nav.lpc' | t }}</span>
              </a>
              <a routerLink="/lifecycle/open-market-cases" [title]="'nav.openMarket' | t">
                <span class="nav-link-mark">OM</span>
                <span class="nav-link-text">{{ 'nav.openMarket' | t }}</span>
              </a>
              <a routerLink="/lifecycle/other-procurement-cases" [title]="'nav.otherProcurement' | t">
                <span class="nav-link-mark">OP</span>
                <span class="nav-link-text">{{ 'nav.otherProcurement' | t }}</span>
              </a>
            </div>
          </div>

          <div class="nav-section" *ngIf="auth.hasAccess('tendering')">
            <button
              type="button"
              class="nav-section-toggle"
              (click)="toggleSection('tendering')"
              [attr.aria-expanded]="!isSectionCollapsed('tendering')"
              [title]="'nav.tenderingProcessTracker' | t">
              <span class="nav-group-text">{{ 'nav.tenderingProcessTracker' | t }}</span>
              <span class="nav-chevron">{{ isSectionCollapsed('tendering') ? '+' : '-' }}</span>
            </button>
            <div class="nav-section-links" *ngIf="!isSectionCollapsed('tendering')">
              <a routerLink="/lifecycle/tenders" [title]="'nav.tenderBidRegister' | t">
                <span class="nav-link-mark">TB</span>
                <span class="nav-link-text">{{ 'nav.tenderBidRegister' | t }}</span>
              </a>
              <a routerLink="/lifecycle/emds" [title]="'nav.emdTracker' | t">
                <span class="nav-link-mark">EM</span>
                <span class="nav-link-text">{{ 'nav.emdTracker' | t }}</span>
              </a>
              <a routerLink="/lifecycle/samples" [title]="'nav.sampleRegister' | t">
                <span class="nav-link-mark">SR</span>
                <span class="nav-link-text">{{ 'nav.sampleRegister' | t }}</span>
              </a>
              <a routerLink="/lifecycle/technical-evaluations" [title]="'nav.technicalEvaluation' | t">
                <span class="nav-link-mark">TE</span>
                <span class="nav-link-text">{{ 'nav.technicalEvaluation' | t }}</span>
              </a>
              <a routerLink="/lifecycle/financial-evaluations" [title]="'nav.financialEvaluation' | t">
                <span class="nav-link-mark">FE</span>
                <span class="nav-link-text">{{ 'nav.financialEvaluation' | t }}</span>
              </a>
              <a routerLink="/lifecycle/purchase-orders" [title]="'nav.purchaseOrder' | t">
                <span class="nav-link-mark">PO</span>
                <span class="nav-link-text">{{ 'nav.purchaseOrder' | t }}</span>
              </a>
              <a routerLink="/lifecycle/performance-securities" [title]="'nav.performanceSecurity' | t">
                <span class="nav-link-mark">PS</span>
                <span class="nav-link-text">{{ 'nav.performanceSecurity' | t }}</span>
              </a>
              <a routerLink="/lifecycle/delivery-acceptance" [title]="'nav.deliveryAcceptance' | t">
                <span class="nav-link-mark">DA</span>
                <span class="nav-link-text">{{ 'nav.deliveryAcceptance' | t }}</span>
              </a>
              <a routerLink="/lifecycle/payments" [title]="'nav.paymentTracker' | t">
                <span class="nav-link-mark">PT</span>
                <span class="nav-link-text">{{ 'nav.paymentTracker' | t }}</span>
              </a>
            </div>
          </div>

          <div class="nav-section">
            <button
              type="button"
              class="nav-section-toggle"
              (click)="toggleSection('tools')"
              [attr.aria-expanded]="!isSectionCollapsed('tools')"
              [title]="'nav.tools' | t">
              <span class="nav-group-text">{{ 'nav.tools' | t }}</span>
              <span class="nav-chevron">{{ isSectionCollapsed('tools') ? '+' : '-' }}</span>
            </button>
            <div class="nav-section-links" *ngIf="!isSectionCollapsed('tools')">
              <a routerLink="/reports" *ngIf="auth.hasAccess('reports')" [title]="'nav.reports' | t">
                <span class="nav-link-mark">RP</span>
                <span class="nav-link-text">{{ 'nav.reports' | t }}</span>
              </a>
              <a routerLink="/users" *ngIf="auth.currentUser?.role === 'admin'" [title]="'nav.userManagement' | t">
                <span class="nav-link-mark">UM</span>
                <span class="nav-link-text">{{ 'nav.userManagement' | t }}</span>
              </a>
              <a routerLink="/theme" [title]="'nav.changeTheme' | t">
                <span class="nav-link-mark">CT</span>
                <span class="nav-link-text">{{ 'nav.changeTheme' | t }}</span>
              </a>
              <a routerLink="/profile" [title]="'nav.myProfile' | t">
                <span class="nav-link-mark">MP</span>
                <span class="nav-link-text">{{ 'nav.myProfile' | t }}</span>
              </a>
            </div>
          </div>
        </nav>
      </aside>
      <main class="content">
        <header class="topbar">
          <div>
            <span class="muted">{{ 'app.signedInAs' | t }}</span>
            <strong>{{ auth.currentUser?.full_name }}</strong>
          </div>
          <label class="language-switcher">
            <span>{{ 'app.language' | t }}</span>
            <select [ngModel]="i18n.language" (ngModelChange)="i18n.setLanguage($event)">
              <option *ngFor="let language of i18n.languages" [ngValue]="language.code">{{ language.label }}</option>
            </select>
          </label>
          <button type="button" (click)="auth.logout()">{{ 'app.logout' | t }}</button>
        </header>
        <router-outlet></router-outlet>
      </main>
    </div>
    <ng-template #publicPage>
      <router-outlet></router-outlet>
    </ng-template>
  `
})
export class AppComponent {
  readonly auth = inject(AuthService);
  readonly i18n = inject(I18nService);
  private readonly theme = inject(ThemeService);

  sidebarCollapsed = false;
  private readonly collapsedSections = new Set<string>();

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  toggleSection(section: string): void {
    if (this.collapsedSections.has(section)) {
      this.collapsedSections.delete(section);
      return;
    }

    this.collapsedSections.add(section);
  }

  isSectionCollapsed(section: string): boolean {
    return this.collapsedSections.has(section);
  }
}
